import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    DASHBOARD_SECTION_NAV_ITEMS,
    type DashboardSectionPanelId,
} from '../dashboardSectionNav';
import { DashboardSectionSlideRail } from './DashboardSectionSlideRail';
import { DashboardSplitMainPanels } from './DashboardSplitMainPanels';
import { DashboardV3MainPanels } from './DashboardV3MainPanels';
import { useCurrentUser } from '../../../../../contexts/UserContext';
import { useViewerStore } from '../../../../../store/store';
import { useDashboardStoreBindings } from '../hooks/useDashboardStoreBindings';

/** Collapsed rail: full dashboard + AI designs (plus 3D cube & opp validation in slide rail). */
const RAIL_PRIMARY_IDS: DashboardSectionPanelId[] = ['all', 'designs'];

interface DashboardMainCardsSectionProps {
    theme: 'dark' | 'light';
    siteAddress: any;
    titleSearchData: any;
    oppRefreshKey: number;
    setAddTitleContactsModalOpen: (next: boolean) => void;
    handleOpenBlackKnightLeadModal: () => void;
    dashboardAnnualUsage: number | null;
    dashboardSystemSizeKw: number | null;
    dashboardAvgDailyUsage: number | null;
    dashboardMultiplier: number | null;
    triggerPricingGridForDesign: (design: any) => void;
    priceQuote: any;
    isDashboardOpen: boolean;
    pricingFetchTrigger: number | null;
    pricingDesignForTrigger: any;
    setPricingFetchTrigger: React.Dispatch<React.SetStateAction<number | null>>;
    opportunityDetails: any;
    selectedDesignForPricing: any;
    setPricingGridModalOpen: (next: boolean) => void;
    opportunityID: any;
    dashboardSectionPanel: DashboardSectionPanelId;
    setDashboardSectionPanel: React.Dispatch<React.SetStateAction<DashboardSectionPanelId>>;
    designsDashboardSlot: React.ReactNode;
    ahjTown: string;
    setbackNotes: string[];
    setAhjModalOpen: (next: boolean) => void;
    setOppValidationModalOpen: (next: boolean) => void;
    setIsDashboardOpen: (next: boolean | ((prev: boolean) => boolean)) => void;
    initialDashboardReady: boolean;
}

export const DashboardMainCardsSection: React.FC<DashboardMainCardsSectionProps> = (props) => {
    const {
        theme,
        siteAddress,
        titleSearchData,
        oppRefreshKey,
        setAddTitleContactsModalOpen,
        handleOpenBlackKnightLeadModal,
        dashboardAnnualUsage,
        dashboardSystemSizeKw,
        dashboardAvgDailyUsage,
        dashboardMultiplier,
        triggerPricingGridForDesign,
        priceQuote,
        isDashboardOpen,
        pricingFetchTrigger,
        pricingDesignForTrigger,
        setPricingFetchTrigger,
        opportunityDetails,
        selectedDesignForPricing,
        setPricingGridModalOpen,
        opportunityID,
        dashboardSectionPanel,
        setDashboardSectionPanel,
        designsDashboardSlot,
        ahjTown,
        setbackNotes,
        setAhjModalOpen,
        setOppValidationModalOpen,
        setIsDashboardOpen,
        initialDashboardReady,
    } = props;

    const showAhjTool = Boolean(ahjTown || (setbackNotes && setbackNotes.length > 0));

    const [sectionRailExpanded, setSectionRailExpanded] = useState(false);
    const closeAiDesignsModalRef = useRef<(() => void) | null>(null);

    const { hasCapability } = useCurrentUser();
    const dashboardLayoutVariant = useViewerStore((s) => s.dashboardLayoutVariant);
    const isV3Layout = dashboardLayoutVariant === 'v3';

    const { loadingCompleted, refreshingShadeFromStore, designListFromStore, siteDesignDetailsFromStore } =
        useDashboardStoreBindings({});
    const designAndShadeReady = Boolean(loadingCompleted && !refreshingShadeFromStore);
    const hasPrimaryDesign = Array.isArray(designListFromStore) && designListFromStore.length > 0;
    const hasSiteDesignDetails = Array.isArray(siteDesignDetailsFromStore) && siteDesignDetailsFromStore.length > 0;
    const isDashboardDataLoading = !designAndShadeReady && !hasPrimaryDesign && !hasSiteDesignDetails;

    const opportunityNumericId = useMemo(
        () => opportunityID ?? opportunityDetails?.Opportunity_Id ?? opportunityDetails?.OpportunityId ?? null,
        [opportunityID, opportunityDetails],
    );

    const salesForceId =
        opportunityDetails?.SalesForce_Id ??
        (typeof window !== 'undefined' ? sessionStorage.getItem('SalesForceId') : null) ??
        undefined;

    const onSolarPricingClick = useCallback(() => {
        if (selectedDesignForPricing) {
            triggerPricingGridForDesign(selectedDesignForPricing);
            return;
        }
        setPricingGridModalOpen(true);
        setPricingFetchTrigger(Date.now());
    }, [
        selectedDesignForPricing,
        setPricingFetchTrigger,
        setPricingGridModalOpen,
        triggerPricingGridForDesign,
    ]);

    useEffect(() => {
        const portraitMq = window.matchMedia('(max-width: 1024px) and (orientation: portrait)');
        const mobileFloatingMenuMq = window.matchMedia('(max-width: 820px)');
        const syncRailExpansionForViewport = () => {
            if (portraitMq.matches && !mobileFloatingMenuMq.matches) {
                setSectionRailExpanded(true);
            }
            if (mobileFloatingMenuMq.matches) {
                setSectionRailExpanded(false);
            }
        };

        syncRailExpansionForViewport();
        portraitMq.addEventListener('change', syncRailExpansionForViewport);
        mobileFloatingMenuMq.addEventListener('change', syncRailExpansionForViewport);
        return () => {
            portraitMq.removeEventListener('change', syncRailExpansionForViewport);
            mobileFloatingMenuMq.removeEventListener('change', syncRailExpansionForViewport);
        };
    }, []);

    const filteredNavItems = DASHBOARD_SECTION_NAV_ITEMS.filter((item) => {
        if (!hasCapability('Show_AI_Scenarios') && item.id === 'designs') return false;
        return true;
    });

    const collapsedRailNavItems = filteredNavItems.filter((item) => RAIL_PRIMARY_IDS.includes(item.id));
    const railNavItems =
        sectionRailExpanded
            ? filteredNavItems
            : (collapsedRailNavItems.length > 0 ? collapsedRailNavItems : filteredNavItems.slice(0, 1));

    useEffect(() => {
        if (!sectionRailExpanded && !RAIL_PRIMARY_IDS.includes(dashboardSectionPanel)) {
            setDashboardSectionPanel('all');
        }
    }, [sectionRailExpanded, dashboardSectionPanel, setDashboardSectionPanel]);

    const sectionRail = (
        <div
            className={`obd-dashboard-rail-portal-root obd-dashboard-v3 obd-dashboard-bento obd-theme-${theme}`}
        >
            <DashboardSectionSlideRail
                sectionRailExpanded={sectionRailExpanded}
                setSectionRailExpanded={setSectionRailExpanded}
                railNavItems={railNavItems}
                dashboardSectionPanel={dashboardSectionPanel}
                setDashboardSectionPanel={setDashboardSectionPanel}
                opportunityID={opportunityID}
                showAhjTool={showAhjTool}
                setOppValidationModalOpen={setOppValidationModalOpen}
                setAhjModalOpen={setAhjModalOpen}
                setIsDashboardOpen={setIsDashboardOpen}
                onDesignsRailSelect={() => {
                    closeAiDesignsModalRef.current?.();
                }}
            />
        </div>
    );

    return (
        <>
            {isDashboardOpen && initialDashboardReady && hasCapability('Show_OneButton') && !isV3Layout
                ? createPortal(sectionRail, document.body)
                : null}
            <div
                className={`obd-dashboard-split obd-dashboard-glass-wrap obd-theme-${theme}${isV3Layout ? ' obd-dashboard-split--v3-layout' : ' obd-dashboard-split--section-nav obd-dashboard-split--rail-floating'}`}
            >
                {isV3Layout ? (
                    <DashboardV3MainPanels
                        theme={theme}
                        siteAddress={siteAddress}
                        titleSearchData={titleSearchData}
                        oppRefreshKey={oppRefreshKey}
                        setAddTitleContactsModalOpen={setAddTitleContactsModalOpen}
                        handleOpenBlackKnightLeadModal={handleOpenBlackKnightLeadModal}
                        dashboardAnnualUsage={dashboardAnnualUsage}
                        dashboardSystemSizeKw={dashboardSystemSizeKw}
                        dashboardAvgDailyUsage={dashboardAvgDailyUsage}
                        dashboardMultiplier={dashboardMultiplier}
                        triggerPricingGridForDesign={triggerPricingGridForDesign}
                        priceQuote={priceQuote}
                        isDashboardOpen={isDashboardOpen}
                        pricingFetchTrigger={pricingFetchTrigger}
                        pricingDesignForTrigger={pricingDesignForTrigger}
                        setPricingFetchTrigger={setPricingFetchTrigger}
                        opportunityDetails={opportunityDetails}
                        selectedDesignForPricing={selectedDesignForPricing}
                        setPricingGridModalOpen={setPricingGridModalOpen}
                        opportunityID={opportunityID}
                        dashboardSectionPanel={dashboardSectionPanel}
                        setDashboardSectionPanel={setDashboardSectionPanel}
                        designsDashboardSlot={designsDashboardSlot}
                        setIsDashboardOpen={setIsDashboardOpen}
                    />
                ) : (
                <DashboardSplitMainPanels
                    theme={theme}
                    siteAddress={siteAddress}
                    titleSearchData={titleSearchData}
                    oppRefreshKey={oppRefreshKey}
                    setAddTitleContactsModalOpen={setAddTitleContactsModalOpen}
                    handleOpenBlackKnightLeadModal={handleOpenBlackKnightLeadModal}
                    dashboardAnnualUsage={dashboardAnnualUsage}
                    dashboardSystemSizeKw={dashboardSystemSizeKw}
                    dashboardAvgDailyUsage={dashboardAvgDailyUsage}
                    dashboardMultiplier={dashboardMultiplier}
                    triggerPricingGridForDesign={triggerPricingGridForDesign}
                    priceQuote={priceQuote}
                    isDashboardOpen={isDashboardOpen}
                    pricingFetchTrigger={pricingFetchTrigger}
                    pricingDesignForTrigger={pricingDesignForTrigger}
                    setPricingFetchTrigger={setPricingFetchTrigger}
                    opportunityDetails={opportunityDetails}
                    selectedDesignForPricing={selectedDesignForPricing}
                    setPricingGridModalOpen={setPricingGridModalOpen}
                    opportunityID={opportunityID}
                    dashboardSectionPanel={dashboardSectionPanel}
                    setDashboardSectionPanel={setDashboardSectionPanel}
                    designsDashboardSlot={designsDashboardSlot}
                    setIsDashboardOpen={setIsDashboardOpen}
                    registerCloseAiDesignsModal={(close) => {
                        closeAiDesignsModalRef.current = close;
                    }}
                />
                )}
            </div>
        </>
    );
};
